import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Button from '../../components/ui/Button'
import { useActiveMenu } from '../nav/useActiveMenu'
import { useBreadcrumbTrail } from '../nav/breadcrumb'
import { HOME_PATH } from '../nav/menu'
import PersonaBadge from '../../components/PersonaBadge'
import { useProxyPersonaMenu } from '../../features/proxy/useProxyPersonaMenu'
import AssistantDialog from '../../features/assistant/AssistantDialog'
import { useAssistantMenu } from '../../features/assistant/useAssistantMenu'
import NotificationsButton from '../../components/NotificationsButton'
import LanguageSwitch from './LanguageSwitch'
import { useProxyMode } from '../../features/proxy/useProxyMode'
import { personaLabelWithAuthority } from '../../features/proxy/personaLabel'
import { useDisplayName } from '../../auth/useCurrentUser'

interface TopbarProps {
  /** Mobil hamburger → drawer aç. */
  onMenuToggle: () => void
}

/**
 * Üst bar — Figma: breadcrumb (grid ikon + modül / sayfa) + ikon seti + dil + avatar.
 */
export default function Topbar({ onMenuToggle }: TopbarProps) {
  const { t } = useTranslation()
  const active = useActiveMenu()
  const trail = useBreadcrumbTrail()
  const proxyMenuItems = useProxyPersonaMenu()
  // Kimlik menüsü iki kaynaktan beslenir: vekalet personaları (089) ve asistan tanımlama (126).
  // Birleştirme BURADA yapılır — PersonaBadge maddeleri çağırandan alır ve ikisini de bilmez.
  const assistant = useAssistantMenu()
  // Rozet etiketi: persona kipindeyken adın yanına yetki seviyesi de yazılır (126 FR-015). Metin
  // menü satırıyla AYNI fonksiyondan gelir — ikisi ayrı yazılsaydı biri güncellenip diğeri
  // unutulurdu. Vekalet kipinde ve kendi personasında ek yoktur, yani 089 bugünküyle aynı kalır.
  const proxy = useProxyMode()
  const displayName = useDisplayName() ?? ''
  const personaLabel = personaLabelWithAuthority(t, displayName, proxy.active ? proxy.kind : null)

  // Menü sonrası kuyruk: aktif sayfa öğesi + sayfa-özel ek adımlar (grup/toplantı). Son öğe kalın (mevcut);
  // öncekiler `to` varsa link. Böylece iç içe adımlar tek (header) breadcrumb'ta gezinilebilir olur.
  // Modül adımı: başlıksız modülde kural olarak yazılmaz — ama kuyruk boşsa tek kalan içerik odur,
  // yazılmazsa breadcrumb'ta hiçbir metin kalmaz (aşağıdaki yorumda gerekçe).
  const tail = active
    ? [
        ...(active.item
          ? [
              {
                // Breadcrumb'a özel ad varsa onu kullan (ör. "Arşivlenenler › Toplantılar" → "Arşivlenen Toplantılar").
                label: t(active.item.breadcrumbLabelKey ?? active.item.labelKey),
                to: active.item.path,
              },
            ]
          : []),
        ...trail,
      ]
    : []
  const showModuleCrumb = active != null && (!active.module.flat || tail.length === 0)

  return (
    <header className="topbar">
      <div className="topbar-left">
        <Button
          className="menu-toggle"
          icon="pi pi-bars"
          text
          rounded
          aria-label={t('shell.menu')}
          onClick={onMenuToggle}
        />
        <nav className="crumb" aria-label="breadcrumb">
          {/* Figma breadcrumb: ev (home) ikonu — anasayfaya yönlendirir. */}
          <Link to={HOME_PATH} className="crumb-home" aria-label={t('shell.home')}>
            <svg
              className="crumb-lead"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M2 6.5 L8 1.75 L14 6.5" />
              <path d="M3.5 5.5 V14 H12.5 V5.5" />
            </svg>
          </Link>
          {active && (
            <>
              {/* Başlıksız modülde (ör. Tutanak Takip) modül adımı ATLANIR: adı sol menüde de
                  çizilmiyor, breadcrumb'ta bırakmak menüde bulunamayan bir başlık gösterirdi
                  (kullanıcı kararı 2026-09-18). Ayraç da onunla birlikte gider — dışarıda kalsaydı
                  ev ikonundan sonra "› ›" çıkardı (kuyruğun kendi ayracı zaten var).

                  TEK İSTİSNA — kuyruk boşsa modül adı YİNE yazılır: menüde karşılığı olmayan ve kendi
                  breadcrumb'ını basmayan sayfalarda (ör. Alt Görev Detay, `/minutes/subactions/:id`)
                  atlamak breadcrumb'ı tamamen boşaltıyordu; geriye yalnız ev ikonu kalıyor, kullanıcı
                  nerede olduğunu hiç göremiyordu (push kapısı code review bulgusu). */}
              {showModuleCrumb && (
                <>
                  <span className="crumb-sep" aria-hidden>
                    ›
                  </span>
                  <span className="crumb-mod">{t(active.module.labelKey)}</span>
                </>
              )}
              {tail.map((seg, i) => {
                const isLast = i === tail.length - 1
                return (
                  <Fragment key={`${i}:${seg.label}`}>
                    <span className="crumb-sep" aria-hidden>
                      ›
                    </span>
                    {isLast || !seg.to ? (
                      <b>{seg.label}</b>
                    ) : (
                      <Link to={seg.to} className="crumb-link">
                        {seg.label}
                      </Link>
                    )}
                  </Fragment>
                )
              })}
            </>
          )}
        </nav>
      </div>

      <div className="topbar-right">
        {/* <button type="button" className="topbar-ic" aria-label="apps">
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
            <rect x="2.5" y="2.5" width="5.5" height="5.5" rx="1.2" />
            <rect x="12" y="2.5" width="5.5" height="5.5" rx="1.2" />
            <rect x="2.5" y="12" width="5.5" height="5.5" rx="1.2" />
            <rect x="12" y="12" width="5.5" height="5.5" rx="1.2" />
          </svg>
        </button>
        <button type="button" className="topbar-ic" aria-label="help">
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
            <circle cx="10" cy="10" r="7.5" />
            <path d="M7.8 7.6a2.2 2.2 0 114 1.3c-.8.7-1.8.9-1.8 2M10 14.2h.01" />
          </svg>
        </button>
        <NotificationsButton />
        <button type="button" className="topbar-ic" aria-label="settings">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
        <button type="button" className="topbar-ic" aria-label="new">
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
            <path d="M10 4v12M4 10h12" />
          </svg>
        </button> */}
        <NotificationsButton />
        <LanguageSwitch />
        {/* Vekalet persona seçimi (089) kimlik menüsünün içindedir — ayrı bir seçim kutusu YOKTUR
            (2026-08-23 kararı, gerekçe `useProxyPersonaMenu`'de). NOT: vekil modunda rozet HEDEFİN
            adını gösterir (`/me.displayName` etkin kimliktir) — "rozet giriş yapanı, menü adına
            çalışılanı gösterir" diye bir ayrım YOKTUR. Gerçek kullanıcının kim olduğu bilgisi
            `/me.proxy.actingDisplayName`'de durur ve yalnız uyarı şeridine aittir. Vekaleti olmayan
            kullanıcıda hook boş dizi döner → menü bugünküyle birebir aynı kalır. */}
        <PersonaBadge
          variant="topbar"
          label={personaLabel}
          extraMenuItems={[...proxyMenuItems, ...assistant.items]}
        />
        <AssistantDialog visible={assistant.dialogVisible} onHide={assistant.closeDialog} />
      </div>
    </header>
  )
}
